import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getCateringCalendarView } from "../../api/get/getCateringCalendarViewApi";
import { getCateringOrderDetailsApi } from "../../api/get/getCateringOrderDetailsApi";
import CateringOrderDetailsModal from "../../components/modal/CateringOrderDetailsModal";
import PendingPaymentWarningModal, { usePendingPaymentCheck } from "../../components/modal/PendingPaymentWarningModal";
import { 
  setCateringCartFilters, 
  addCateringCartPackage, 
  setCateringRestaurantId,
  clearCateringCartItems,
  setCateringItemsToRestore,
  clearCateringItemsToRestore,
  setIsPastOrderRestoration,
  setModifyOrderMode,
  setUnpaidOrderMode,
  setPastOrderAddressId,
  clearPendingPayment,
  setCateringDeliveryCharge,
  addItemToCateringPackage
} from "../../redux/features/restaurants/restaurantSlice";
import { selectPendingPayment } from "../../redux/features/restaurants/restaurantSelectors";
import { getCateringPackagesAsync, getRestaurantItemsCategoriesAsync, getCateringPackageDetailAsync } from "../../redux/features/restaurants/restaurantThunks";
import { setCateringOrderDetails } from "../../redux/features/cateringCustomer/cateringCustomerSlice";
import { getCateringOrderDetailsAsync } from "../../redux/features/cateringCustomer/cateringCustomerThunks";
import { selectCateringOrderDetails } from "../../redux/features/cateringCustomer/cateringCustomerSelectors";
import { formatDateForInput } from "../../utils/common";
import { restoreOrder } from "../../utils/orderRestoration";

export const CateringCalendarView = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("month"); // "month", "week", or "today"
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
  
  // Brand details modal state
  const [isBrandModalOpen, setIsBrandModalOpen] = useState(false);
  
  // Filter states
  const [selectedBrands, setSelectedBrands] = useState(new Set());
  const [selectedServiceItems, setSelectedServiceItems] = useState(new Set());
  const [selectedStatuses, setSelectedStatuses] = useState(new Set());
  const [selectedOrderTypes, setSelectedOrderTypes] = useState(new Set()); // combined vs non-combined
  
  // Track pending order action
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const [pendingActionType, setPendingActionType] = useState(null);
  
  // Use the pending payment check hook
  const { checkAndProceed, showWarning: showPendingPaymentWarning, handleProceed: handleConfirmPendingPayment, handleCancel: handleCancelPendingPayment } = usePendingPaymentCheck();
  
  // Redux selectors
  const pendingPayment = useSelector(selectPendingPayment);
  const cateringOrderDetails = useSelector(selectCateringOrderDetails);

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth(); // 0-11

  // Fetch calendar data
  useEffect(() => {
    const fetchCalendarData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getCateringCalendarView(
          currentYear,
          currentMonth + 1 // API expects 1-12
        );
        setCalendarData(data.data);
      } catch (err) {
        setError("Failed to load calendar data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCalendarData();
  }, [currentYear, currentMonth]);

  // Monitor cateringOrderDetails changes when loading (similar to past order card)
  useEffect(() => {
    console.log('🔍 useEffect triggered - loadingOrderDetails:', loadingOrderDetails, 'pendingOrderId:', pendingOrderId, 'pendingActionType:', pendingActionType);
    console.log('🔍 cateringOrderDetails:', cateringOrderDetails);
    
    const handleOrderDetailsLoaded = async () => {
      if (loadingOrderDetails && pendingOrderId && pendingActionType) {
        console.log('🔍 Checking order details match...');
        console.log('🔍 cateringOrderDetails exists?', !!cateringOrderDetails);
        console.log('🔍 cateringOrderDetails has keys?', cateringOrderDetails ? Object.keys(cateringOrderDetails).length : 0);
        console.log('🔍 Comparing - cateringOrderDetails.order_no:', cateringOrderDetails?.order_no, 'with stored order_id:', pendingOrderId);
        
        // Match by order_no (string) instead of order_id (number)
        if (
          cateringOrderDetails &&
          Object.keys(cateringOrderDetails).length > 0 &&
          cateringOrderDetails.order_no
        ) {
          console.log('✅ Order details loaded from Redux:', cateringOrderDetails);
          console.log('✅ Pending action type:', pendingActionType);
          
          // Handle different action types
          if (pendingActionType === "modify") {
            console.log('🔄 Triggering modify order flow');
            await handleModifyOrder(cateringOrderDetails);
          } else if (pendingActionType === "continue") {
            console.log('💳 Triggering continue payment flow');
            await handlePendingPaymentOrder(cateringOrderDetails);
          } else if (pendingActionType === "unpaid") {
            console.log('📝 Triggering unpaid order flow');
            await handleUnpaidOrder(cateringOrderDetails);
          }
          
          // Clear pending state after processing
          setPendingOrderId(null);
          setPendingActionType(null);
        }
      }
    };
    
    handleOrderDetailsLoaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cateringOrderDetails, loadingOrderDetails, pendingOrderId, pendingActionType]);

  // Navigation handlers
  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };

  const goToCurrentMonth = () => {
    setCurrentDate(new Date());
  };

  const handleTodayView = () => {
    setCurrentDate(new Date());
    setViewMode("today");
  };

  const handleWeekView = () => {
    setViewMode("week");
  };

  const handleMonthView = () => {
    setViewMode("month");
  };

  const handlePreviousWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const handleBackNavigation = () => {
    navigate(-1); // Go back to previous page
  };

  // Transform API response to match modal's expected format
  const transformOrderDetails = (apiData) => {
    if (!apiData) return null;

    console.log("=== API Data Received ===", apiData);

    // Check if this is a combined order
    const isCombinedOrder = apiData.is_combine_order === "yes" || apiData.is_combined_order === "yes";

    // Handle both customer and company addresses
    let addressDetails = null;
    let customerPhone = null;
    let customerPhoneCountry = null;
    let deliveryAreaName = null;
    
    if (apiData.userType === "customer" && apiData.user_address) {
      // Customer address from user_address
      const addr = apiData.user_address;
      console.log("Using user_address:", addr);
      addressDetails = {
        addressType: addr.address_type,
        block: addr.block,
        street: addr.street,
        avenue: addr.avenue,
        houseNo: addr.house_no,
        building: addr.building,
        floor: addr.floor,
        apartmentNo: addr.apartment_no,
        companyName: addr.company_name,
        mobileNumber: addr.mobile_number,
        countryCode: addr.country_code,
      };
      // Extract phone and area from user_address
      customerPhone = addr.phone_no;
      customerPhoneCountry = addr.phone_country_code;
      deliveryAreaName = addr.delivery_charge_area_name_en;
    } else if (apiData.userType === "customer" && apiData.customerAddress) {
      // Customer address from customerAddress (calendar API response)
      const addr = apiData.customerAddress;
      console.log("Using customerAddress:", addr);
      addressDetails = {
        addressType: addr.address_type,
        block: addr.block,
        street: addr.street,
        avenue: addr.avenue,
        houseNo: addr.house_no,
        building: addr.building,
        floor: addr.floor,
        apartmentNo: addr.apartment_no,
        companyName: addr.company_name,
        mobileNumber: addr.mobile_number,
        countryCode: addr.country_code,
      };
      // Extract phone and area from customerAddress
      customerPhone = addr.phone_no;
      customerPhoneCountry = addr.phone_country_code;
      deliveryAreaName = addr.delivery_charge_area_name_en;
    } else if (apiData.userType === "company" && apiData.customerAddress) {
      // Company address
      const addr = apiData.customerAddress;
      console.log("Using company customerAddress:", addr);
      addressDetails = {
        address1: addr.address1,
        address2: addr.address2,
      };
      // Extract phone and area from company customerAddress
      customerPhone = addr.phone_no;
      customerPhoneCountry = addr.phone_country_code;
      deliveryAreaName = addr.delivery_charge_area_name_en;
    }

    // Also check top level for phone and area (fallback)
    if (!customerPhone) customerPhone = apiData.phone_no;
    if (!customerPhoneCountry) customerPhoneCountry = apiData.phone_country_code;
    if (!deliveryAreaName) deliveryAreaName = apiData.delivery_charge_area_name_en;

    console.log("Extracted Phone:", customerPhone);
    console.log("Extracted Country Code:", customerPhoneCountry);
    console.log("Extracted Area:", deliveryAreaName);

    return {
      orderNo: apiData.order_no,
      restaurant: apiData.restaurant_name_en,
      serviceItemName: apiData.service_item_name_en,
      customerName: `${apiData.customer_first_name || ''} ${apiData.customer_last_name || ''}`.trim(),
      customerPhoneNumber: customerPhone,
      customerPhoneCountryCode: customerPhoneCountry,
      deliveryArea: deliveryAreaName,
      orderDate: apiData.order_date,
      serviceItemDate: apiData.service_item_date,
      serviceItemSlot: apiData.service_item_slot,
      noOfPax: apiData.no_of_pax,
      orderStatus: apiData.order_status,
      paymentMethod: apiData.payment_method_en,
      updatedByAgentId: apiData.updated_by_agent_id || "",
      updatedDatetime: apiData.updated_datetime || "",
      serviceItems: apiData.service_item || [],
      notes: apiData.special_instructions_1 || apiData.special_instructions_2 || apiData.comments || "",
      service_item_price: apiData.service_item_price,
      order_total_price: apiData.order_total_price,
      total_price: apiData.order_total_price,
      currency: apiData.currency,
      paymentProcessingFee: apiData.payment_processing_fee || 0,
      deliveryCharges: apiData.delivery_charge || 0,
      userType: apiData.userType,
      addressDetails: addressDetails,
      // Bilbayt information
      is_bilbayt: apiData.is_bilbayt || 0,
      bilbayt_price: apiData.bilbayt_price || "",
      bilbayt_discount: apiData.bilbayt_discount || "",
      bilbayt_order_id: apiData.bilbayt_order_id || "",
      // Combined order specific fields
      isCombineOrder: isCombinedOrder,
      combineOrders: apiData.combine_orders || [],
      combineOrderId: apiData.combine_order_id || apiData.combine_catering_order_id || null,
    };
  };

  // Handle event click - fetch order details and show modal
  const handleEventClick = async (orderId, isCombineOrder = "no", combineOrderId = null) => {
    if (!orderId) return;
    
    setLoadingOrderDetails(true);
    try {
      // For combined orders, use combine_order_id if available
      const idToFetch = (isCombineOrder === "yes" && combineOrderId) ? combineOrderId : orderId;
      
      console.log('Fetching order details for modal:', {
        orderId,
        combineOrderId,
        isCombineOrder,
        idToFetch
      });
      
      const response = await getCateringOrderDetailsApi(idToFetch, isCombineOrder);
      console.log("API Response:", response);
      
      // Handle both response structures: response.data or direct response
      const orderData = response?.data || response;
      console.log("Order Data:", orderData);
      
      if (orderData) {
        const transformedData = transformOrderDetails(orderData);
        console.log("Transformed Data:", transformedData);
        setSelectedOrderDetails(transformedData);
        setIsModalOpen(true);
      }
    } catch (err) {
      console.error("Failed to load order details:", err);
      // Optionally show an error message to the user
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  // Close modal handler
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOrderDetails(null);
  };

  // Handle order action (modify/continue payment)
  const handleOrderAction = async (event, action) => {
    // Prevent event bubbling
    event.preventDefault();
    event.stopPropagation();

    const orderId = event.currentTarget.dataset.orderId;
    const isCombineOrder = event.currentTarget.dataset.isCombineOrder || "no";
    const combineOrderId = event.currentTarget.dataset.combineOrderId || null;
    
    console.log('handleOrderAction called:', { orderId, isCombineOrder, combineOrderId, action });
    
    if (!orderId) {
      console.error('No orderId found in dataset');
      return;
    }

    // Check for pending payment with API verification
    const canProceed = await checkAndProceed(async () => {
      console.log('Proceeding with order action after confirmation');
      await proceedWithOrderAction(orderId, isCombineOrder, combineOrderId, action);
    });

    // If canProceed is true, execute immediately; otherwise modal will show
    if (canProceed) {
      console.log('Proceeding with order action immediately');
      await proceedWithOrderAction(orderId, isCombineOrder, combineOrderId, action);
    }
  };

  const proceedWithOrderAction = async (orderId, isCombineOrder, combineOrderId, action) => {
    try {
      setLoadingOrderDetails(true);
      
      // For combined orders: 
      // - If combineOrderId exists, use it
      // - If not, use the regular orderId with is_combine_order flag
      // The backend will return the full combined order data when is_combine_order="yes"
      const idToFetch = (isCombineOrder === "yes" && combineOrderId && combineOrderId !== "") 
        ? combineOrderId 
        : orderId;
      
      // Set pending state BEFORE fetching
      setPendingOrderId(idToFetch);
      setPendingActionType(action);
      
      // Clear any stale order details to ensure fresh data
      console.log('Clearing stale order details from Redux');
      dispatch(setCateringOrderDetails(null));
      
      // Fetch complete order details - the useEffect will handle the rest
      console.log('📞 Fetching order details for order:', {
        orderId,
        combineOrderId,
        isCombineOrder,
        idToFetch,
        action,
        note: isCombineOrder === "yes" ? 'Fetching as combined order' : 'Fetching as single order'
      });
      
      // IMPORTANT: Always pass is_combine_order flag to API
      await dispatch(getCateringOrderDetailsAsync(idToFetch, isCombineOrder));
      
      // The useEffect will monitor cateringOrderDetails and trigger the appropriate handler
      console.log('✅ API call completed, useEffect will handle the restoration');
    } catch (error) {
      console.error("Error handling order action:", error);
      setLoadingOrderDetails(false);
      setPendingOrderId(null);
      setPendingActionType(null);
    }
  };


  // Handler for modify order action (similar to past order card)
  const handleModifyOrder = async (orderData) => {
    try {
      console.log('Starting modify order mode with order details:', orderData);
      
      // Check if this is a combined order and get the combine_catering_order_id
      const isCombined = orderData.is_combine_order === "yes" || orderData.is_combined_order === "yes";
      const combineCateringOrderId = isCombined ? (orderData.combine_catering_order_id || orderData.combine_order_id) : null;
      
      console.log('🔒 Modify order mode setup:', {
        orderId: orderData.order_id,
        isCombined,
        combineCateringOrderId
      });
      
      // Parse the original total from order details
      const originalTotal = parseFloat(orderData.total_price || orderData.order_total_price || 0);
      
      // Pass modify order data to handleOrderRestoration
      await handleOrderRestoration(orderData, true, {
        modifyOrderPax: parseInt(orderData.no_of_pax || 20),
        modifyOrderId: orderData.order_id,
        modifyOrderOriginalTotal: originalTotal,
        combineOrderId: combineCateringOrderId
      });
    } catch (error) {
      console.error('Error setting up modify order mode:', error);
      setLoadingOrderDetails(false);
    }
  };

  // Handler for unpaid order action
  const handleUnpaidOrder = async (orderData) => {
    try {
      console.log('Starting unpaid order mode with order details:', orderData);
      
      // Check if this is a combined order and get the combine_catering_order_id
      const isCombined = orderData.is_combine_order === "yes" || orderData.is_combined_order === "yes";
      const combineCateringOrderId = isCombined ? (orderData.combine_catering_order_id || orderData.combine_order_id) : null;
      
      console.log('💳 Unpaid order mode setup:', {
        orderId: orderData.order_id,
        isCombined,
        combineCateringOrderId
      });
      
      // Pass unpaid order data to handleOrderRestoration
      await handleOrderRestoration(orderData, false, null, true, {
        unpaidOrderId: orderData.order_id,
        combineOrderId: combineCateringOrderId
      });
    } catch (error) {
      console.error('Error setting up unpaid order mode:', error);
      setLoadingOrderDetails(false);
    }
  };

  // Handler for pending payment order action
  const handlePendingPaymentOrder = async (orderData) => {
    try {
      console.log('Starting cart restoration with order details:', orderData);
      
      // Not in modify mode for pending payment orders (pass null for modifyModeData)
      await handleOrderRestoration(orderData, false, null);
    } catch (error) {
      console.error('Error restoring pending payment order:', error);
      setLoadingOrderDetails(false);
    }
  };

  // Main order restoration handler (similar to past order card)
  const handleOrderRestoration = async (orderData, isModifyMode, modifyModeData = null, isUnpaidMode = false, unpaidModeData = null) => {
    try {
      // Use the shared restoration function from orderRestoration.js
      const result = await restoreOrder({
        orderData,
        isModifyMode,
        modifyModeData,
        isUnpaidMode,
        unpaidModeData,
        isReorderMode: false,
        reorderModeData: null,
        dispatch,
        navigate,
        setLoadingState: setLoadingOrderDetails,
        actions: {
          setModifyOrderMode,
          setUnpaidOrderMode,
          setReorderMode: () => {}, // Not used in calendar
          clearCateringItemsToRestore,
          clearCateringCartItems,
          setCateringCartFilters,
          setPastOrderAddressId,
          getCateringPackagesAsync,
          getRestaurantItemsCategoriesAsync,
          getCateringPackageDetailAsync,
          setCateringRestaurantId,
          addCateringCartPackage,
          addItemToCateringPackage,
          setCateringItemsToRestore,
          setIsPastOrderRestoration,
          setCateringDeliveryCharge
        },
        fullRestoration: true // CRITICAL: Must be true for combined orders to restore items!
      });
      
      if (!result.success) {
        console.error('Order restoration failed:', result.error);
        setLoadingOrderDetails(false);
      }
    } catch (error) {
      console.error('Error in order restoration:', error);
      setLoadingOrderDetails(false);
    }
  };


  // Extract and format time from eventTime string
  const formatTime = (eventTimeString) => {
    // The eventTime comes as "2025-10-12T11:00 AM - 11:30 AMZ"
    // We want to extract just the time portion: "11:00 AM - 11:30 AM"
    if (!eventTimeString) return "";
    
    // Remove the "Z" at the end if present
    const cleanedTime = eventTimeString.replace(/Z$/, "");
    
    // Extract the time part after the date
    const timePart = cleanedTime.split("T")[1];
    return timePart || cleanedTime;
  };

  // Parse event time for sorting (extract first time from range)
  const getEventSortTime = (eventTimeString) => {
    if (!eventTimeString) return 0;
    
    // Extract date and first time from string like "2025-10-12T11:00 AM - 11:30 AMZ"
    const dateMatch = eventTimeString.match(/(\d{4}-\d{2}-\d{2})/);
    const timeMatch = eventTimeString.match(/T(\d{1,2}:\d{2}\s*(?:AM|PM))/i);
    
    if (dateMatch && timeMatch) {
      const dateStr = dateMatch[1];
      const timeStr = timeMatch[1].trim();
      
      // Convert to 24-hour format for proper sorting
      const [time, period] = timeStr.split(/\s+/);
      const [hours, minutes] = time.split(':').map(Number);
      let adjustedHours = hours;
      
      if (period && period.toUpperCase() === 'PM' && hours !== 12) {
        adjustedHours = hours + 12;
      } else if (period && period.toUpperCase() === 'AM' && hours === 12) {
        adjustedHours = 0;
      }
      
      return new Date(`${dateStr}T${String(adjustedHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`).getTime();
    }
    
    return 0;
  };

  // Get today's date string
  const getTodayDateString = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  };

  // Get week range for current date
  const getWeekRange = () => {
    const curr = new Date(currentDate);
    const first = curr.getDate() - curr.getDay(); // First day is Sunday
    const last = first + 6; // Last day is Saturday

    const firstDay = new Date(curr.setDate(first));
    const lastDay = new Date(curr.setDate(last));

    return { firstDay, lastDay };
  };

  // Get formatted week range string
  const getWeekRangeString = () => {
    const { firstDay, lastDay } = getWeekRange();
    const firstMonth = monthNames[firstDay.getMonth()];
    const lastMonth = monthNames[lastDay.getMonth()];
    
    if (firstDay.getMonth() === lastDay.getMonth()) {
      return `${firstMonth} ${firstDay.getDate()} - ${lastDay.getDate()}, ${firstDay.getFullYear()}`;
    } else {
      return `${firstMonth} ${firstDay.getDate()} - ${lastMonth} ${lastDay.getDate()}, ${firstDay.getFullYear()}`;
    }
  };

  // Get calendar grid
  const getCalendarGrid = () => {
    if (!calendarData || !calendarData.days) return [];

    // Create a map of dates to events
    const dateEventsMap = {};
    calendarData.days.forEach((day) => {
      dateEventsMap[day.date] = day;
    });

    // If in today view, return only today's data
    if (viewMode === "today") {
      const todayDateString = getTodayDateString();
      const todayData = dateEventsMap[todayDateString];
      const today = new Date();
      
      return [[{
        date: today.getDate(),
        dateString: todayDateString,
        dayData: todayData,
      }]];
    }

    // If in week view, return current week's data
    if (viewMode === "week") {
      const { firstDay, lastDay } = getWeekRange();
      const weekData = [];
      
      for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
        const dateString = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        const dayData = dateEventsMap[dateString];
        
        weekData.push({
          date: d.getDate(),
          dateString,
          dayData,
          fullDate: new Date(d),
        });
      }
      
      return [weekData];
    }

    // Month view logic
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const startDay = firstDayOfMonth.getDay(); // 0 (Sunday) to 6 (Saturday)
    const daysInMonth = lastDayOfMonth.getDate();

    // Build calendar grid
    const grid = [];
    let currentWeek = [];

    // Add empty cells for days before the first day of month
    for (let i = 0; i < startDay; i++) {
      currentWeek.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = `${currentYear}-${String(currentMonth + 1).padStart(
        2,
        "0"
      )}-${String(day).padStart(2, "0")}`;
      const dayData = dateEventsMap[dateString];

      currentWeek.push({
        date: day,
        dateString,
        dayData,
      });

      // If we've filled a week (7 days), push it to grid and start new week
      if (currentWeek.length === 7) {
        grid.push(currentWeek);
        currentWeek = [];
      }
    }

    // Add remaining days to complete the last week
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      grid.push(currentWeek);
    }

    return grid;
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  // Get all unique brands, service items, and statuses from calendar data with counts
  const getFilterOptions = () => {
    if (!calendarData || !calendarData.days) return { 
      brands: [], 
      serviceItems: [], 
      statuses: [],
      orderTypes: [],
      brandCounts: {},
      serviceItemCounts: {},
      statusCounts: {},
      orderTypeCounts: {}
    };

    const brandCounts = {};
    const serviceItemCounts = {};
    const statusCounts = {};
    const orderTypeCounts = { "Combined": 0, "Single": 0 };

    const processEvents = (events) => {
      events.forEach(event => {
        // Filter out cancelled orders
        const status = event.status?.toLowerCase();
        if (status === "cancelled" || status === "canceled") return;

        if (event.restaurant_name_en) {
          brandCounts[event.restaurant_name_en] = (brandCounts[event.restaurant_name_en] || 0) + 1;
        }
        
        // Handle eventType as both array and string
        if (event.eventType) {
          if (Array.isArray(event.eventType)) {
            // For combined orders with multiple event types
            event.eventType.forEach(type => {
              serviceItemCounts[type] = (serviceItemCounts[type] || 0) + 1;
            });
          } else {
            // For single orders with string event type
            serviceItemCounts[event.eventType] = (serviceItemCounts[event.eventType] || 0) + 1;
          }
        }
        
        if (event.status) {
          statusCounts[event.status] = (statusCounts[event.status] || 0) + 1;
        }
        
        // Count order types (combined vs single)
        const isCombined = event.is_combine_order === "yes" || event.is_combine_order === true;
        if (isCombined) {
          orderTypeCounts["Combined"]++;
        } else {
          orderTypeCounts["Single"]++;
        }
      });
    };

    if (viewMode === "today") {
      const todayDateString = getTodayDateString();
      const todayData = calendarData.days.find(day => day.date === todayDateString);
      if (todayData && todayData.events) {
        processEvents(todayData.events);
      }
    } else if (viewMode === "week") {
      const { firstDay, lastDay } = getWeekRange();
      calendarData.days.forEach(day => {
        const dayDate = new Date(day.date);
        if (dayDate >= firstDay && dayDate <= lastDay && day.events) {
          processEvents(day.events);
        }
      });
    } else {
      calendarData.days.forEach(day => {
        if (day.events) {
          processEvents(day.events);
        }
      });
    }

    return {
      brands: Object.keys(brandCounts).sort(),
      serviceItems: Object.keys(serviceItemCounts).sort(),
      statuses: Object.keys(statusCounts).sort(),
      orderTypes: Object.keys(orderTypeCounts).filter(type => orderTypeCounts[type] > 0),
      brandCounts,
      serviceItemCounts,
      statusCounts,
      orderTypeCounts
    };
  };

  const { brands, serviceItems, statuses, orderTypes, brandCounts, serviceItemCounts, statusCounts, orderTypeCounts } = getFilterOptions();

  // Calculate brand counts based on view mode and filters
  const getBrandCounts = () => {
    if (!calendarData || !calendarData.days) return { totalBrands: 0, totalEvents: 0, brandDetails: {} };

    let brandDetails = {}; // { brandName: count }
    let totalEvents = 0;

    const filterEvent = (event) => {
      // Filter out cancelled orders
      const status = event.status?.toLowerCase();
      if (status === "cancelled" || status === "canceled") return false;

      // Apply brand filter
      if (selectedBrands.size > 0 && !selectedBrands.has(event.restaurant_name_en)) return false;

      // Apply service item filter - handle both array and string eventType
      if (selectedServiceItems.size > 0) {
        if (Array.isArray(event.eventType)) {
          // For combined orders, check if any of the event types match
          const hasMatch = event.eventType.some(type => selectedServiceItems.has(type));
          if (!hasMatch) return false;
        } else {
          // For single orders, check direct match
          if (!selectedServiceItems.has(event.eventType)) return false;
        }
      }

      // Apply status filter
      if (selectedStatuses.size > 0 && !selectedStatuses.has(event.status)) return false;

      // Apply order type filter (combined vs single)
      if (selectedOrderTypes.size > 0) {
        const isCombined = event.is_combine_order === "yes" || event.is_combine_order === true;
        const orderType = isCombined ? "Combined" : "Single";
        if (!selectedOrderTypes.has(orderType)) return false;
      }

      return true;
    };

    const processEvents = (events) => {
      events.forEach(event => {
        if (filterEvent(event)) {
          totalEvents++;
          if (event.eventType) {
            brandDetails[event.eventType] = (brandDetails[event.eventType] || 0) + 1;
          }
        }
      });
    };

    if (viewMode === "today") {
      const todayDateString = getTodayDateString();
      const todayData = calendarData.days.find(day => day.date === todayDateString);
      if (todayData && todayData.events) {
        processEvents(todayData.events);
      }
    } else if (viewMode === "week") {
      const { firstDay, lastDay } = getWeekRange();
      calendarData.days.forEach(day => {
        const dayDate = new Date(day.date);
        if (dayDate >= firstDay && dayDate <= lastDay && day.events) {
          processEvents(day.events);
        }
      });
    } else {
      calendarData.days.forEach(day => {
        if (day.events) {
          processEvents(day.events);
        }
      });
    }

    return { 
      totalBrands: Object.keys(brandDetails).length, 
      totalEvents,
      brandDetails 
    };
  };

  const { totalBrands, totalEvents, brandDetails } = getBrandCounts();

  // Handle brand pill click
  const handleBrandClick = () => {
    setIsBrandModalOpen(true);
  };

  // Close brand modal
  const handleCloseBrandModal = () => {
    setIsBrandModalOpen(false);
  };

  // Filter toggle handlers
  const toggleBrandFilter = (brand) => {
    setSelectedBrands(prev => {
      const newSet = new Set(prev);
      if (newSet.has(brand)) {
        newSet.delete(brand);
      } else {
        newSet.add(brand);
      }
      return newSet;
    });
  };

  const toggleServiceItemFilter = (serviceItem) => {
    setSelectedServiceItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(serviceItem)) {
        newSet.delete(serviceItem);
      } else {
        newSet.add(serviceItem);
      }
      return newSet;
    });
  };

  const toggleStatusFilter = (status) => {
    setSelectedStatuses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(status)) {
        newSet.delete(status);
      } else {
        newSet.add(status);
      }
      return newSet;
    });
  };

  const toggleOrderTypeFilter = (orderType) => {
    setSelectedOrderTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderType)) {
        newSet.delete(orderType);
      } else {
        newSet.add(orderType);
      }
      return newSet;
    });
  };

  const clearAllFilters = () => {
    setSelectedBrands(new Set());
    setSelectedServiceItems(new Set());
    setSelectedStatuses(new Set());
    setSelectedOrderTypes(new Set());
  };

  // Get status-based styling for event cards
  const getStatusStyling = (status, isBilbayt = false) => {
    if (!status) return {
      bg: "bg-gray-100",
      border: "border-gray-300",
      hover: "hover:bg-gray-200",
      text: "text-gray-800"
    };

    const normalizedStatus = status.toLowerCase();

    // Bilbayt status - Teal/Cyan with special styling (complements Bilbayt logo)
    if (normalizedStatus === "bilbayt") {
      return {
        bg: "bg-cyan-50",
        border: "border-cyan-300",
        hover: "hover:bg-cyan-100",
        text: "text-cyan-900"
      };
    }

    // Paid orders - Green (or Teal/Cyan for Bilbayt)
    if (normalizedStatus === "paid") {
      if (isBilbayt) {
        return {
          bg: "bg-cyan-50",
          border: "border-cyan-300",
          hover: "hover:bg-cyan-100",
          text: "text-cyan-900"
        };
      }
      return {
        bg: "bg-green-100",
        border: "border-green-300",
        hover: "hover:bg-green-200",
        text: "text-green-800"
      };
    }
    
    // Pending Payment - Yellow/Amber
    if (normalizedStatus === "pending payment") {
      return {
        bg: "bg-yellow-100",
        border: "border-yellow-300",
        hover: "hover:bg-yellow-200",
        text: "text-yellow-800"
      };
    }
    
    // Booked waiting payment - Blue
    if (normalizedStatus === "booked waiting payment") {
      return {
        bg: "bg-blue-100",
        border: "border-blue-300",
        hover: "hover:bg-blue-200",
        text: "text-blue-800"
      };
    }

    // Default styling for any other status
    return {
      bg: "bg-gray-100",
      border: "border-gray-300",
      hover: "hover:bg-gray-200",
      text: "text-gray-800"
    };
  };

  return (
    <div className="p-4 min-h-screen bg-slate-200">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          {/* Back Button */}
          <button
            onClick={handleBackNavigation}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </button>
          
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Catering Orders Calendar</h1>
            {/* Brand and Event Count */}
            <div className="flex gap-4 mt-2 text-sm">
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full font-semibold">
                {totalEvents} {totalEvents === 1 ? 'Order' : 'Orders'}
              </span>
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-semibold">
                {viewMode === "today" ? "Today's View" : viewMode === "week" ? "Week View" : "Month View"}
              </span>
            </div>
          </div>
        </div>
        
        <div className="flex gap-2">
          {viewMode !== "today" && (
            <button
              onClick={handleTodayView}
              className="px-4 py-2 bg-amber-800 text-white rounded-lg hover:bg-amber-900 transition"
            >
              Today
            </button>
          )}
          {viewMode !== "week" && (
            <button
              onClick={handleWeekView}
              className="px-4 py-2 bg-amber-800 text-white rounded-lg hover:bg-amber-900 transition"
            >
              Week
            </button>
          )}
          {viewMode !== "month" && (
            <button
              onClick={handleMonthView}
              className="px-4 py-2 bg-amber-800 text-white rounded-lg hover:bg-amber-900 transition"
            >
              Month
            </button>
          )}
        </div>
      </div>

      {/* Status Legend */}
      <div className="mb-4 bg-white p-4 rounded-lg shadow">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Order Status Legend:</h3>
        <div className="flex flex-wrap gap-3 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-100 border border-green-300"></div>
            <span className="text-gray-700">Paid</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-cyan-50 border border-cyan-300"></div>
            <span className="text-gray-700 flex items-center gap-1">
              Bilbayt
              <img src="/Images/bilbayt.jpeg" alt="Bilbayt" className="w-3 h-3 object-contain" />
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-yellow-100 border border-yellow-300"></div>
            <span className="text-gray-700">Pending Payment</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-blue-100 border border-blue-300"></div>
            <span className="text-gray-700">Booked Waiting Payment</span>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="mb-4 bg-white p-4 rounded-lg shadow">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-semibold text-gray-700">Filters:</h3>
          {(selectedBrands.size > 0 || selectedServiceItems.size > 0 || selectedStatuses.size > 0 || selectedOrderTypes.size > 0) && (
            <button
              onClick={clearAllFilters}
              className="text-xs text-red-600 hover:text-red-800 font-semibold underline"
            >
              Clear All Filters
            </button>
          )}
        </div>

        {/* Brand Filters */}
        {brands.length > 0 && (
          <div className="mb-3">
            <h4 className="text-xs font-semibold text-gray-600 mb-2">Brands:</h4>
            <div className="flex flex-wrap gap-2">
              {brands.map(brand => (
                <button
                  key={brand}
                  onClick={() => toggleBrandFilter(brand)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition flex items-center gap-1.5 ${
                    selectedBrands.has(brand)
                      ? 'bg-amber-800 text-white hover:bg-amber-900'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                  }`}
                >
                  <span>{brand}</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                    selectedBrands.has(brand)
                      ? 'bg-amber-950 text-white'
                      : 'bg-gray-300 text-gray-800'
                  }`}>
                    {brandCounts[brand] || 0}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Service Item Filters */}
        {serviceItems.length > 0 && (
          <div className="mb-3">
            <h4 className="text-xs font-semibold text-gray-600 mb-2">Service Items:</h4>
            <div className="flex flex-wrap gap-2">
              {serviceItems.map(item => (
                <button
                  key={item}
                  onClick={() => toggleServiceItemFilter(item)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition flex items-center gap-1.5 ${
                    selectedServiceItems.has(item)
                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                  }`}
                >
                  <span>{item}</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                    selectedServiceItems.has(item)
                      ? 'bg-purple-800 text-white'
                      : 'bg-gray-300 text-gray-800'
                  }`}>
                    {serviceItemCounts[item] || 0}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Status Filters */}
        {statuses.length > 0 && (
          <div className="mb-3">
            <h4 className="text-xs font-semibold text-gray-600 mb-2">Status:</h4>
            <div className="flex flex-wrap gap-2">
              {statuses.map(status => {
                const styling = getStatusStyling(status);
                return (
                  <button
                    key={status}
                    onClick={() => toggleStatusFilter(status)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition border flex items-center gap-1.5 ${
                      selectedStatuses.has(status)
                        ? `${styling.bg} ${styling.text} ${styling.border} ring-2 ring-offset-1 ring-gray-400`
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'
                    }`}
                  >
                    <span>{status}</span>
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                      selectedStatuses.has(status)
                        ? `${styling.bg} border ${styling.border} opacity-80`
                        : 'bg-gray-300 text-gray-800'
                    }`}>
                      {statusCounts[status] || 0}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Order Type Filters (Combined vs Single) */}
        {orderTypes.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-600 mb-2">Order Type:</h4>
            <div className="flex flex-wrap gap-2">
              {orderTypes.map(orderType => (
                <button
                  key={orderType}
                  onClick={() => toggleOrderTypeFilter(orderType)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition flex items-center gap-1.5 ${
                    selectedOrderTypes.has(orderType)
                      ? orderType === "Combined" 
                        ? 'bg-purple-600 text-white hover:bg-purple-700'
                        : 'bg-teal-600 text-white hover:bg-teal-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                  }`}
                >
                  <span>{orderType}</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                    selectedOrderTypes.has(orderType)
                      ? orderType === "Combined"
                        ? 'bg-purple-800 text-white'
                        : 'bg-teal-800 text-white'
                      : 'bg-gray-300 text-gray-800'
                  }`}>
                    {orderTypeCounts[orderType] || 0}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Month Navigation - Only show in month view */}
      {viewMode === "month" && (
        <div className="mb-4 flex justify-between items-center bg-white p-4 rounded-lg shadow">
          <button
            onClick={handlePreviousMonth}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
          >
            ← Previous
          </button>
          <h2 className="text-2xl font-bold text-gray-700">
            {monthNames[currentMonth]} {currentYear}
          </h2>
          <button
            onClick={handleNextMonth}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
          >
            Next →
          </button>
        </div>
      )}

      {/* Week Navigation - Only show in week view */}
      {viewMode === "week" && (
        <div className="mb-4 flex justify-between items-center bg-white p-4 rounded-lg shadow">
          <button
            onClick={handlePreviousWeek}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
          >
            ← Previous Week
          </button>
          <h2 className="text-2xl font-bold text-gray-700">
            {getWeekRangeString()}
          </h2>
          <button
            onClick={handleNextWeek}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
          >
            Next Week →
          </button>
        </div>
      )}

      {/* Today View Title */}
      {viewMode === "today" && (
        <div className="mb-4 bg-white p-4 rounded-lg shadow">
          <h2 className="text-2xl font-bold text-gray-700 text-center">
            Today - {monthNames[currentMonth]} {new Date().getDate()}, {currentYear}
          </h2>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-800"></div>
          <p className="mt-2 text-gray-600">Loading calendar...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          {error}
        </div>
      )}

      {/* Calendar Grid */}
      {!loading && !error && calendarData && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Day Names Header - Show in month and week view */}
          {(viewMode === "month" || viewMode === "week") && (
            <div className="grid grid-cols-7 bg-gray-200 border-b">
              {dayNames.map((day) => (
                <div
                  key={day}
                  className="py-3 text-center font-bold text-gray-700 text-sm border-r last:border-r-0"
                >
                  {day}
                </div>
              ))}
            </div>
          )}

          {/* Calendar Days */}
          {getCalendarGrid().map((week, weekIndex) => (
            <div 
              key={weekIndex} 
              className={viewMode === "today" ? "flex justify-center" : "grid grid-cols-7 border-b last:border-b-0"}
            >
              {week.map((day, dayIndex) => (
                <div
                  key={`${weekIndex}-${dayIndex}`}
                  className={`${
                    viewMode === "today" 
                      ? "w-full max-w-4xl p-6 bg-white" 
                      : viewMode === "week"
                      ? "border-r last:border-r-0 min-h-[200px] p-3 bg-white hover:bg-gray-50 transition"
                      : "border-r last:border-r-0 min-h-[150px] p-2 bg-white hover:bg-gray-50 transition"
                  }`}
                >
                  {day && (
                    <>
                      {/* Day Number */}
                      <div className="flex justify-between items-start mb-3">
                        {viewMode === "today" ? (
                          <span className="text-2xl font-semibold text-gray-600">
                            {dayNames[new Date().getDay()]}
                          </span>
                        ) : viewMode === "week" ? (
                          <div className="flex flex-col">
                            <span className="text-xs text-gray-500 font-medium">
                              {dayNames[day.fullDate?.getDay() || new Date(day.dateString).getDay()]}
                            </span>
                            <span className="text-lg font-bold text-gray-700">
                              {day.date}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm font-semibold text-gray-600">
                            {day.date}
                          </span>
                        )}
                      </div>

                      {/* Event Count and Total - Prominently displayed */}
                      {day.dayData && day.dayData.events && (() => {
                        const filteredEvents = day.dayData.events.filter(event => {
                          const status = event.status?.toLowerCase();
                          if (status === "cancelled" || status === "canceled") return false;
                          if (selectedBrands.size > 0 && !selectedBrands.has(event.restaurant_name_en)) return false;
                          
                          // Handle array eventType for combined orders
                          if (selectedServiceItems.size > 0) {
                            if (Array.isArray(event.eventType)) {
                              const hasMatch = event.eventType.some(type => selectedServiceItems.has(type));
                              if (!hasMatch) return false;
                            } else {
                              if (!selectedServiceItems.has(event.eventType)) return false;
                            }
                          }
                          
                          if (selectedStatuses.size > 0 && !selectedStatuses.has(event.status)) return false;
                          
                          // Apply order type filter
                          if (selectedOrderTypes.size > 0) {
                            const isCombined = event.is_combine_order === "yes" || event.is_combine_order === true;
                            const orderType = isCombined ? "Combined" : "Single";
                            if (!selectedOrderTypes.has(orderType)) return false;
                          }
                          
                          return true;
                        });
                        
                        const filteredCount = filteredEvents.length;
                        const totalAmount = filteredEvents.reduce((sum, event) => {
                          // Handle price string format like "175.000 KWD"
                          const priceString = event.order_total_price || "0";
                          const price = parseFloat(priceString.replace(/[^\d.]/g, '')) || 0;
                          return sum + price;
                        }, 0);
                        
                        return filteredCount > 0 ? (
                          <div className={`mb-3 bg-amber-800 text-white py-2 px-3 rounded font-bold ${viewMode === "today" ? "text-lg" : "text-sm"}`}>
                            <div className="text-center">
                              {filteredCount} {filteredCount === 1 ? 'Order' : 'Orders'}
                            </div>
                            <div className={`text-center mt-1 ${viewMode === "today" ? "text-base" : "text-xs"} opacity-90`}>
                              {totalAmount.toFixed(3)} KWD
                            </div>
                          </div>
                        ) : null;
                      })()}

                      {/* No Events Message for Today View */}
                      {viewMode === "today" && (!day.dayData || !day.dayData.events || day.dayData.events.length === 0) && (
                        <div className="text-center py-12 text-gray-500">
                          <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="text-xl font-semibold">No events scheduled for today</p>
                        </div>
                      )}

                      {/* Events List */}
                      {day.dayData && day.dayData.events && (
                        <div className={`space-y-2 ${viewMode === "today" ? "max-h-[600px]" : viewMode === "week" ? "max-h-[180px]" : "max-h-[120px]"} overflow-y-auto`}>
                          {day.dayData.events
                            .filter(event => {
                              // Filter out cancelled orders
                              const status = event.status?.toLowerCase();
                              if (status === "cancelled" || status === "canceled") return false;

                              // Apply brand filter
                              if (selectedBrands.size > 0 && !selectedBrands.has(event.restaurant_name_en)) return false;

                              // Apply service item filter - handle both array and string eventType
                              if (selectedServiceItems.size > 0) {
                                if (Array.isArray(event.eventType)) {
                                  const hasMatch = event.eventType.some(type => selectedServiceItems.has(type));
                                  if (!hasMatch) return false;
                                } else {
                                  if (!selectedServiceItems.has(event.eventType)) return false;
                                }
                              }

                              // Apply status filter
                              if (selectedStatuses.size > 0 && !selectedStatuses.has(event.status)) return false;

                              // Apply order type filter
                              if (selectedOrderTypes.size > 0) {
                                const isCombined = event.is_combine_order === "yes" || event.is_combine_order === true;
                                const orderType = isCombined ? "Combined" : "Single";
                                if (!selectedOrderTypes.has(orderType)) return false;
                              }

                              return true;
                            })
                            .sort(
                              (a, b) =>
                                getEventSortTime(a.eventTime) -
                                getEventSortTime(b.eventTime)
                            )
                            .map((event) => {
                              // Check if this is a Bilbayt order - check multiple possible field names
                              // Looking at API response: is_bilbayt can be true/false, payment_method_en/ar can be "Bilbayt"
                              const isBilbayt = event.is_bilbayt === 1 || 
                                               event.is_bilbayt === true || 
                                               event.is_bilbayt === "1" ||
                                               event.payment_method?.toLowerCase()?.includes('bilbayt') ||
                                               event.paymentMethod?.toLowerCase()?.includes('bilbayt') ||
                                               event.payment_method_en?.toLowerCase()?.includes('bilbayt') ||
                                               event.payment_method_ar?.toLowerCase()?.includes('bilbayt');
                              
                              // Debug log for first few events
                              if (event.id % 10 === 0) {
                                console.log('Event Debug:', {
                                  id: event.id,
                                  status: event.status,
                                  is_bilbayt: event.is_bilbayt,
                                  payment_method: event.payment_method,
                                  payment_method_en: event.payment_method_en,
                                  payment_method_ar: event.payment_method_ar,
                                  isBilbaytDetected: isBilbayt
                                });
                              }
                              
                              const styling = getStatusStyling(event.status, isBilbayt);
                              
                              // Determine which action button to show based on status
                              const normalizedStatus = event.status?.toLowerCase();
                              const showModifyButton = normalizedStatus === "paid" || normalizedStatus === "bilbayt";
                              const showContinueButton = normalizedStatus === "pending payment";
                              const showUnpaidButton = normalizedStatus === "submitted" || normalizedStatus === "accepted" || normalizedStatus === "booked waiting for payment";
                              
                              // Check if this is a combined order
                              const isCombinedOrder = event.is_combine_order === "yes" || event.is_combine_order === true;
                              
                              return (
                                <div
                                  key={event.id}
                                  className={`${viewMode === "today" ? "text-base p-4" : "text-xs p-2"} rounded transition-all hover:shadow-md ${
                                    isCombinedOrder ? 'border-2 border-purple-300 bg-purple-50' : `${styling.bg} border ${styling.border} ${styling.hover}`
                                  }`}
                                >
                                  {/* Header with Order No and Status */}
                                  <div className="flex justify-between items-start mb-1">
                                    <div 
                                      onClick={() => handleEventClick(event.order_id, event.is_combine_order || "no", event.combine_order_id || event.combine_catering_order_id || null)}
                                      className={`font-semibold ${styling.text} ${viewMode === "today" ? "text-lg" : ""} cursor-pointer hover:underline flex-1`}
                                    >
                                      {formatTime(event.eventTime)}
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                      {isCombinedOrder && (
                                        <span className="px-1.5 py-0.5 bg-purple-600 text-white text-[9px] font-bold rounded uppercase whitespace-nowrap">
                                          COMBINED
                                        </span>
                                      )}
                                      {event.status && (
                                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${styling.bg} ${styling.text} border ${styling.border} flex items-center gap-1`}>
                                          {event.status}
                                          {(normalizedStatus === "bilbayt" || (isBilbayt && normalizedStatus === "paid")) && (
                                            <img src="/Images/bilbayt.jpeg" alt="Bilbayt" className="w-2.5 h-2.5 object-contain inline-block" />
                                          )}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Order Number */}
                                  {event.order_no && (
                                    <div 
                                      onClick={() => handleEventClick(event.order_id, event.is_combine_order || "no", event.combine_order_id || event.combine_catering_order_id || null)}
                                      className={`text-gray-600 text-[10px] mb-1 cursor-pointer font-medium ${isCombinedOrder ? 'text-purple-700' : ''}`}
                                    >
                                      Order #{event.order_no}
                                    </div>
                                  )}
                                  
                                  {/* Service Item Name or Combined Packages */}
                                  {!isCombinedOrder ? (
                                    <div 
                                      onClick={() => handleEventClick(event.order_id, event.is_combine_order || "no", event.combine_order_id || event.combine_catering_order_id || null)}
                                      className="text-gray-700 truncate font-medium cursor-pointer" 
                                      title={Array.isArray(event.eventType) ? event.eventType.join(', ') : event.eventType}
                                    >
                                      {Array.isArray(event.eventType) ? event.eventType.join(', ') : event.eventType}
                                    </div>
                                  ) : (
                                    <div 
                                      onClick={() => handleEventClick(event.order_id, event.is_combine_order || "no", event.combine_order_id || event.combine_catering_order_id || null)}
                                      className="text-purple-800 font-bold text-[11px] mb-1 cursor-pointer"
                                      title={Array.isArray(event.eventType) ? event.eventType.join(', ') : ''}
                                    >
                                      {Array.isArray(event.eventType) ? event.eventType.join(', ') : `${event.combine_orders?.length || 'Multiple'} Package${(event.combine_orders?.length || 2) !== 1 ? 's' : ''}`}
                                    </div>
                                  )}
                                  
                                  {/* Customer Name */}
                                  <div 
                                    onClick={() => handleEventClick(event.order_id, event.is_combine_order || "no", event.combine_order_id || event.combine_catering_order_id || null)}
                                    className="text-gray-600 truncate cursor-pointer" 
                                    title={event.customerName}
                                  >
                                    {event.customerName}
                                  </div>
                                  
                                  {/* Mobile Number */}
                                  {event.mobileNumber && (
                                    <div 
                                      onClick={() => handleEventClick(event.order_id, event.is_combine_order || "no", event.combine_order_id || event.combine_catering_order_id || null)}
                                      className="text-gray-500 truncate text-[11px] cursor-pointer"
                                    >
                                      {event.mobileNumber}
                                    </div>
                                  )}
                                  
                                  {/* Action Buttons */}
                                  {(showModifyButton || showContinueButton || showUnpaidButton) && (
                                    <div className={`${viewMode === "today" ? "mt-3" : "mt-2"} pt-2 border-t border-gray-300`}>
                                      {showModifyButton && (
                                        <button
                                          data-order-id={event.order_id}
                                          data-is-combine-order={event.is_combine_order || "no"}
                                          data-combine-order-id={event.combine_order_id || event.combine_catering_order_id || ""}
                                          onClick={(e) => handleOrderAction(e, "modify")}
                                          className={`w-full ${viewMode === "today" ? "text-xs py-2" : "text-[10px] py-1"} font-semibold rounded bg-blue-500 text-white hover:bg-blue-600 transition-colors`}
                                        >
                                          {viewMode === "today" ? "View or Modify" : "Modify"}
                                        </button>
                                      )}
                                      {showContinueButton && (
                                        <button
                                          data-order-id={event.order_id}
                                          data-is-combine-order={event.is_combine_order || "no"}
                                          data-combine-order-id={event.combine_order_id || event.combine_catering_order_id || ""}
                                          onClick={(e) => handleOrderAction(e, "continue")}
                                          className={`w-full ${viewMode === "today" ? "text-xs py-2" : "text-[10px] py-1"} font-semibold rounded bg-orange-500 text-white hover:bg-orange-600 transition-colors`}
                                        >
                                          {viewMode === "today" ? "Continue Payment" : "Continue"}
                                        </button>
                                      )}
                                      {showUnpaidButton && (
                                        <button
                                          data-order-id={event.order_id}
                                          data-is-combine-order={event.is_combine_order || "no"}
                                          data-combine-order-id={event.combine_order_id || event.combine_catering_order_id || ""}
                                          onClick={(e) => handleOrderAction(e, "unpaid")}
                                          className={`w-full ${viewMode === "today" ? "text-xs py-2" : "text-[10px] py-1"} font-semibold rounded bg-amber-600 text-white hover:bg-amber-700 transition-colors`}
                                        >
                                          {viewMode === "today" ? "Modify & Send Payment" : "Modify"}
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Loading Indicator for Order Details */}
      {loadingOrderDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-800 mb-4"></div>
            <p className="text-gray-700 font-medium">Loading order details...</p>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      <CateringOrderDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        orderDetails={selectedOrderDetails}
      />

      {/* Pending Payment Warning Modal */}
      <PendingPaymentWarningModal
        isOpen={showPendingPaymentWarning}
        onClose={handleCancelPendingPayment}
        onProceed={handleConfirmPendingPayment}
        actionDescription="this action"
      />

      {/* Brand Details Modal */}
      {isBrandModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-amber-800 text-white px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold">
                Brand Details - {viewMode === "today" ? "Today" : viewMode === "week" ? getWeekRangeString() : `${monthNames[currentMonth]} ${currentYear}`}
              </h2>
              <button
                onClick={handleCloseBrandModal}
                className="text-white hover:text-gray-200 transition"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
              {/* Summary */}
              <div className="mb-6 p-4 bg-slate-100 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-3xl font-bold text-amber-800">{totalBrands}</p>
                    <p className="text-gray-600 font-medium">{totalBrands === 1 ? 'Brand' : 'Brands'}</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-green-800">{totalEvents}</p>
                    <p className="text-gray-600 font-medium">Total {totalEvents === 1 ? 'Event' : 'Events'}</p>
                  </div>
                </div>
              </div>

              {/* Brand List */}
              {Object.keys(brandDetails).length > 0 ? (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Brands & Event Count</h3>
                  {Object.entries(brandDetails)
                    .sort((a, b) => b[1] - a[1]) // Sort by event count, highest first
                    .map(([brandName, count], index) => (
                      <div
                        key={brandName}
                        className="flex justify-between items-center p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition"
                      >
                        <div className="flex items-center gap-3">
                          <span className="flex items-center justify-center w-8 h-8 bg-amber-800 text-white rounded-full font-bold text-sm">
                            {index + 1}
                          </span>
                          <span className="font-semibold text-gray-800">{brandName}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-bold text-amber-800">{count}</span>
                          <span className="text-gray-600 text-sm">{count === 1 ? 'event' : 'events'}</span>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                  <p className="text-xl font-semibold text-gray-500">No brands found</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-slate-100 px-6 py-4 flex justify-end">
              <button
                onClick={handleCloseBrandModal}
                className="px-6 py-2 bg-amber-800 text-white rounded-lg hover:bg-amber-900 transition font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

